// Row-identity wire types and pure helpers. No browser or axios imports: the
// web worker loads this module too, and a module that touches `window` at top
// level kills the worker (and with it every table-data request).
/** Mirrors `common_core::source::row_identity::RowIdentity` (serde, tagged). */
export type RowIdentity =
  | { kind: "declared_key"; columns: string[] }
  | { kind: "chosen_key"; columns: string[] }
  | { kind: "content_hash" };

/** Mirrors `data_service::api::responses::SyncStatsDto`. */
export interface SyncStats {
  matched: number;
  changed: number;
  new: number;
  gone: number;
  duplicate_key_rows: number;
  null_key_rows: number;
}

export function parseRowIdentityJson(
  json: string | null | undefined,
): RowIdentity | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as RowIdentity;
  } catch {
    return null;
  }
}

/** The columns a table is keyed on, declared or chosen — what the column list
 *  marks. The keyless strategies name none. */
export function keyColumnsOf(ri: RowIdentity | null | undefined): string[] {
  switch (ri?.kind) {
    case "declared_key":
    case "chosen_key":
      return ri.columns;
    default:
      return [];
  }
}

/** `nameOf` turns a key into the columns it names — the one thing a user needs
 *  to see about the key they just picked, and the one thing the bare label
 *  cannot say. */
export function rowIdentityLabel(
  ri: RowIdentity | null | undefined,
  nameOf?: (columnId: string) => string,
): string {
  switch (ri?.kind) {
    case "declared_key":
      return nameOf ? ri.columns.map(nameOf).join(" + ") : "primary key";
    case "chosen_key":
      return nameOf ? ri.columns.map(nameOf).join(" + ") : "chosen key";
    case "content_hash":
      return "row content";
    default:
      return "the source default";
  }
}

/** One-line toast body: "Synced · 3 changed · 1 new · 2 gone".
 *
 *  A key that has stopped telling rows apart is appended: the picker warns
 *  when the key is CHOSEN, but duplicates usually appear later as the source
 *  drifts, and a sync is the only moment the user can see it happen. */
export function describeSyncStats(stats: SyncStats | null | undefined): string {
  if (!stats) return "Sync successful.";
  const parts = [
    stats.changed ? `${stats.changed} changed` : null,
    stats.new ? `${stats.new} new` : null,
    stats.gone ? `${stats.gone} gone` : null,
  ].filter((p): p is string => p !== null);
  // Counts only. A duplicated or null key is a standing property of the
  // table, not news about this sync, and a toast is gone before anyone has
  // read a sentence about it — it belongs where the table's identity is
  // shown, not here.
  return parts.length ? `Synced · ${parts.join(" · ")}` : "Sync successful.";
}

/** Mirrors `data_service::api::sync_commit::IdentityCandidate`. */
export interface IdentityCandidate {
  id: string;
  kind:
    "keep_old_key" | "use_column" | "content_hash" | "continue" | "no_match";
  label: string;
  persist_as: RowIdentity | null;
  preview: { matched: number; changed: number; new: number; gone: number };
  recommended: boolean;
}

/** Mirrors `data_service::api::sync_commit::IdentityConflict` (409 body). */
export interface IdentityConflict {
  kind:
    | "key_removed"
    | "pk_dropped"
    | "key_not_unique"
    | "key_degraded"
    | "low_overlap";
  message: string;
  previous_strategy: RowIdentity | null;
  candidates: IdentityCandidate[];
}

export interface IdentityDecision {
  candidate_id: string;
}

/** A sync the server refused until the user picks how rows are matched. */
export class IdentityConflictError extends Error {
  readonly code = "identity_conflict" as const;
  readonly conflict: IdentityConflict;
  constructor(conflict: IdentityConflict) {
    super(conflict.message);
    this.name = "IdentityConflictError";
    this.conflict = conflict;
  }
}

export function isIdentityConflictError(
  error: unknown,
): error is IdentityConflictError {
  return error instanceof IdentityConflictError;
}

/** Recognise the 409 body `{ error: "identity_conflict", conflict }`. */
export function identityConflictFromBody(
  status: number | undefined,
  data: unknown,
): IdentityConflict | null {
  if (status !== 409 || !data || typeof data !== "object") return null;
  const body = data as { error?: string; conflict?: IdentityConflict };
  if (body.error !== "identity_conflict" || !body.conflict) return null;
  return body.conflict;
}

/** Callback a sync uses to ask the user; resolves with a candidate id or
 *  `null` to cancel. */
export type PromptIdentityConflict = (
  conflict: IdentityConflict,
) => Promise<string | null>;

/** Prefix of the worker error message that carries a 409 `identity_conflict`
 *  body across the worker boundary (`parseIdentityConflictMessage`). */
export const IDENTITY_CONFLICT_PREFIX = "IDENTITY_CONFLICT:";

export function parseIdentityConflictMessage(
  message: string,
): IdentityConflict | null {
  if (!message.startsWith(IDENTITY_CONFLICT_PREFIX)) return null;
  try {
    const body = JSON.parse(message.slice(IDENTITY_CONFLICT_PREFIX.length));
    return identityConflictFromBody(409, body);
  } catch {
    return null;
  }
}

/** Mirrors `data_service::api::row_identity_apis::IdentityCandidateColumn`. */
export interface IdentityCandidateColumn {
  column_id: string;
  name: string | null;
  kind: string;
  total: number;
  non_null: number;
  distinct: number;
  /** One value per DISTINCT row — not the same as "no repeated value". */
  unique: boolean;
  /** Short of that, but distinct enough to key on. The server owns the bar;
   *  the picker only ever offers what comes back true. */
  near_unique: boolean;
  id_like: boolean;
}

export interface IdentityCandidates {
  version: number;
  columns: IdentityCandidateColumn[];
  unique_pairs: [string, string][];
  /** Rows that differ from one another. `total_rows - distinct_rows` is how
   *  many rows the source exported twice — a file problem, not a key problem,
   *  and the reason a repeated value need not cost a column its key. Older
   *  servers omit both. */
  distinct_rows?: number;
  total_rows?: number;
  /** The source's primary key, folded into the snapshot at import. Empty for
   *  keyless sources; older servers omit it. */
  declared_key?: string[];
  /** How that version's rows are tracked, as its snapshot stores it. Absent
   *  ⇒ the source default: `declared_key` when non-empty, else content. */
  row_identity?: RowIdentity | null;
}

/** The identity a version's rows are matched under: what its snapshot
 *  stores, else the source default. */
export function effectiveIdentity(c: IdentityCandidates): RowIdentity {
  if (c.row_identity) return c.row_identity;
  if (c.declared_key?.length)
    return { kind: "declared_key", columns: c.declared_key };
  return { kind: "content_hash" };
}

export function sameIdentity(
  a: RowIdentity | null,
  b: RowIdentity | null,
): boolean {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === "content_hash" || b.kind === "content_hash") return true;
  return (
    a.columns.length === b.columns.length &&
    a.columns.every((c, i) => c === b.columns[i])
  );
}

/** Columns the user may nominate as the key: unique, or distinct enough that
 *  the server vouched for them. Anything else is never offered — a column the
 *  user cannot judge is worse than no choice at all. */
export function keyCandidateColumns(
  c: IdentityCandidates,
): IdentityCandidateColumn[] {
  const declared = new Set(c.declared_key ?? []);
  return c.columns.filter(
    (col) => (col.unique || col.near_unique) && !declared.has(col.column_id),
  );
}

/** Whether to ask after an import. The source's own primary key is the answer
 *  whenever it has one, so the dialog is for tables that arrive without one —
 *  a CSV, a Parquet file, a query with no key — and only when there is
 *  something worth nominating. */
export function shouldAskForPrimaryKey(c: IdentityCandidates): boolean {
  if (c.declared_key?.length) return false;
  return keyCandidateColumns(c).length > 0 || c.unique_pairs.length > 0;
}
